import {
    Car,
    ClipboardCheck,
    DollarSign,
    FileText,
    Radio,
    Shield,
    User,
    Wallet,
    Wrench,
    type LucideIcon,
} from 'lucide-react';

import { ADVANCE_AREA_LABELS, resolveAdvanceArea } from './advance/advanceSteps';
import { INSURANCE_AREA_LABELS, resolveInsuranceArea } from './insurance/insuranceSteps';

/**
 * The six areas of contract setup, in the owner's order.
 *
 * THE ORDER IS FIXED SO THE PAGE CAN BE MEMORISED, and urgency is carried by where a
 * route-less entry LANDS (`resolveLandingArea`), never by re-sorting the rail underneath an
 * admin who has learned where things are. IT OPENS ON THE PERSON: every instrument below names
 * the driver, so an admin who cannot yet say who this contract is written over cannot finish
 * anything beneath it. From there it follows the customer's life rather than ours: the money
 * that arrived (20–30% of deposits precede the contract), the car that is prepared, the cover
 * that is bought, the money clauses that are settled, and the instruments that print all of it,
 * signed last.
 *
 * "Review" IS NO LONGER AN AREA. Activation lives in the manifest footer, visible from day
 * one with the count of unmet gates, so there is nothing left to walk to.
 */
export type SetupArea = 'driver' | 'deposit' | 'vehicle' | 'insurance' | 'financials' | 'agreements';

export interface SetupAreaConfig {
    key: SetupArea;
    label: string;
    /** The question the area answers, in the admin's own words. */
    question: string;
    icon: LucideIcon;
}

export const SETUP_AREAS: SetupAreaConfig[] = [
    {
        key: 'driver',
        label: 'Driver',
        question: 'Do we know the customer well enough to print, and to collect?',
        icon: User,
    },
    {
        key: 'deposit',
        label: 'Deposit',
        question: 'Has the customer put money down, and is it papered?',
        icon: Wallet,
    },
    {
        key: 'vehicle',
        label: 'Vehicle',
        question: 'Is this car ready, and legal, to hand over?',
        icon: Car,
    },
    {
        key: 'insurance',
        label: 'Insurance',
        question: 'Is the car covered, who paid for the cover, did we finance it?',
        icon: Shield,
    },
    {
        key: 'financials',
        label: 'Financials',
        question: 'The money clauses this contract will print.',
        icon: DollarSign,
    },
    {
        key: 'agreements',
        label: 'Agreements',
        question: 'Who signs what, in what order, and is it executed?',
        icon: ClipboardCheck,
    },
];

export const AREA_KEYS: SetupArea[] = SETUP_AREAS.map((area) => area.key);

export const AREA_LABELS: Record<SetupArea, string> = SETUP_AREAS.reduce(
    (labels, area) => ({ ...labels, [area.key]: area.label }),
    {} as Record<SetupArea, string>,
);

/**
 * Every `?step=` value that was ever minted, and the area it now means.
 *
 * NOTHING EXTERNAL MAY BREAK. Merge-field pre-flights, the awaiting-finalization table, saved
 * bookmarks and links pasted into WhatsApp all carry `?step=`; each one redirects to the area
 * route that replaced it. `review` maps to NOTHING deliberately — the review step retired into
 * the manifest footer, so its old link lands on the bare setup route and the landing rule takes
 * the admin wherever the work actually is.
 */
export const LEGACY_STEP_ALIASES: Record<string, SetupArea | null> = {
    vehicle: 'vehicle',
    driver: 'driver',
    financials: 'financials',
    insurance: 'insurance',
    agreements: 'agreements',
    review: null,
};

/**
 * The area a URL fragment names, or null.
 *
 * Null means "no area was named" — an unknown value, a hand-edited path, or `review`. Callers
 * treat all three the same way: show the bare setup route and let the landing rule resolve it,
 * rather than silently pinning the admin to the first area the way `resolveStepIndex` did.
 */
export function resolveArea(value: string | null | undefined): SetupArea | null {
    if (!value) return null;
    const key = value.toLowerCase();
    if (AREA_KEYS.includes(key as SetupArea)) return key as SetupArea;
    if (key in LEGACY_STEP_ALIASES) return LEGACY_STEP_ALIASES[key];
    return null;
}

/** The path an area lives at. One place builds it, so a rename cannot half-land. */
export function areaPath(contractId: number, area: SetupArea | null): string {
    const base = `/dashboard/contracts/${contractId}/setup`;
    return area ? `${base}/${area}` : base;
}

/**
 * The label a "Resolve in …" sentence uses.
 *
 * `blockedStep` and `stepForMergeField` hand back area KEYS, and a sentence that reads
 * "Resolve in agreements" is the machine's word for it. Unknown values fall back to the raw
 * value rather than to a wrong area.
 *
 * THE ADVANCE'S THREE AREAS ARE ASKED SECOND, AND THAT IS A LOOKUP RATHER THAN A WIDENING.
 * `MERGE_FIELD_STEPS` routes the `loan.` namespace to `disbursement` and `repayment` now, so this
 * function is handed keys `SetupArea` has never contained — and a pre-flight that printed
 * "Resolve in disbursement" for want of a second lookup would be reading the machine's word for it
 * back to an admin. `SetupArea` stays the closed union of six: the advance vocabulary is a
 * different one, consulted after this one and never merged into it.
 *
 * AND THE POLICY DOSSIER'S FOUR ARE ASKED THIRD, FOR THE SAME REASON AND IN THAT ORDER. Its rail
 * and its blocked slots hand over `policy`, `premium`, `advance` and `agreement`; the first three
 * are this function's only way of naming them, and `agreement` is answered by the advance
 * vocabulary one line earlier under the identical label, so which of the two claims it makes no
 * difference to the sentence. `insurance` is deliberately NOT re-pointed: it is a `SetupArea`, the
 * first lookup answers it "Insurance", and that is still the right word for the tab a pre-flight
 * naming the step is sent to.
 */
export function labelForArea(value: string | null | undefined): string {
    const area = resolveArea(value);
    if (area) return AREA_LABELS[area];
    const advanceArea = resolveAdvanceArea(value);
    if (advanceArea) return ADVANCE_AREA_LABELS[advanceArea];
    const insuranceArea = resolveInsuranceArea(value);
    if (insuranceArea) return INSURANCE_AREA_LABELS[insuranceArea];
    return value ?? '';
}

/**
 * The six `get_preparation_status` items, in the order the work happens.
 *
 * **THE ODOMETER IS LAST, AND THAT IS THE POINT.** The reading has to be the one taken when the
 * car is finally ready — after servicing and conversion, after the plates, after the tracker.
 * Listing it first (as this config did) invited admins to record a number the workshop then
 * drove past.
 *
 * IT PRINTS ON NOTHING, AND IT IS STILL REQUIRED. The v1 Schedule identifies the Vehicle by make
 * and model, year, colour, registration and condition — no odometer row — so the reading is an
 * operational record rather than a merge field: the handover baseline both sides argue wear,
 * mileage and valuation from later. `sign_contract` refuses without it all the same.
 *
 * `{{vehicle.mileage}}` reads `contracts.vehicle_mileage`, not a vehicle column — help copy
 * must not repeat the namespace's lie.
 */
export const PREP_ITEMS_CONFIG: Record<string, { label: string; icon: LucideIcon; description: string }> = {
    service: { label: 'Service Vehicle', icon: Wrench, description: 'Servicing maintenance' },
    registration: { label: 'Register Vehicle', icon: FileText, description: 'Vehicle registration' },
    roadworthiness: { label: 'Roadworthiness', icon: Shield, description: 'Certificate for road safety' },
    tracker: { label: 'Install Trackers', icon: Radio, description: 'GPS tracker installation (primary + secondary)' },
    mileage: {
        label: 'Odometer at handover',
        icon: Car,
        description: 'The handover reading, taken when the car is finally ready',
    },
};
